import React from "react";
import { Table, Badge, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaFile, FaFont, FaTrash, FaHistory, FaEdit } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";

interface SourcesListTableProps {
    sources: any[];
    sourceStatsMap: Record<number, { chunks: number; chars: number; tokensEst: number }>;
    onDelete: (source: any) => void;
    onShowHistory: (source: any) => void;
    onEdit: (source: any) => void;
    isEditMode: boolean;
}

const SourcesListTable: React.FC<SourcesListTableProps> = ({
    sources,
    sourceStatsMap,
    onDelete,
    onShowHistory,
    onEdit,
    isEditMode,
}) => {
    const { t } = useTranslation();
    return (
        <div className="table-responsive">
            <Table hover className="align-middle">
                <thead>
                    <tr>
                        <th id="source_type_header" style={{ width: "50px" }}>{t("chatbots_edit.type")}</th>
                        <th id="source_title_header">{t("chatbots_edit.title_edit")}</th>
                        <th id="source_lang_header" style={{ width: "100px" }}>{t("chatbots_edit.lang")}</th>
                        <th id="source_status_header" style={{ width: "100px" }}>{t("chatbots_edit.status")}</th>
                        <th id="source_stats_header">{t("chatbots_edit.stats")}</th>
                        <th id="source_updated_header">{t("chatbots_edit.last_updated")}</th>
                        <th id="source_actions_header" style={{ width: "150px", textAlign: "right" }}>{t("chatbots_edit.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {sources.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="text-center text-muted py-4">
                                {t("chatbots_edit.no_sources_found")}
                            </td>
                        </tr>
                    ) : (
                        sources.map((source) => {
                            const stats = sourceStatsMap[source.id];
                            const isFile = (source.type || "").toString().toLowerCase() === "file";

                            return (
                                <tr key={source.id}>
                                    <td className="text-center">
                                        <Icon
                                            icon={isFile ? FaFile : FaFont}
                                            className={isFile ? "text-primary" : "text-info"}
                                            size={18}
                                        />
                                    </td>
                                    <td>
                                        <div className="fw-bold text-dark">{source.title || t("chatbots_edit.untitled")}</div>
                                        <small className="text-muted">ID: #{source.id} • v{source.version}</small>
                                    </td>
                                    <td>
                                        <Badge bg="light" text="dark" className="border">
                                            {source.lang || "en"}
                                        </Badge>
                                    </td>
                                    <td>
                                        {source.is_active ? (
                                            <Badge bg="success">{t("active")}</Badge>
                                        ) : (
                                            <Badge bg="secondary">{t("inactive")}</Badge>
                                        )}
                                    </td>
                                    <td>
                                        {stats ? (
                                            <div style={{ fontSize: "0.85rem" }}>
                                                <div>{stats.chunks} {t("chatbots_edit.chunks")}</div>
                                                <div className="text-muted">~{stats.tokensEst} {t("chatbots_edit.tokens")}</div>
                                            </div>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td className="text-muted" style={{ fontSize: "0.85rem" }}>
                                        {source.updated_at ? new Date(source.updated_at).toLocaleDateString() : "-"}
                                    </td>
                                    <td className="text-end">
                                        <div className="d-flex justify-content-end gap-2">
                                            <OverlayTrigger overlay={<Tooltip>{t("edit")}</Tooltip>}>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="icon-btn"
                                                    onClick={() => onEdit(source)}
                                                    disabled={!isEditMode}
                                                >
                                                    <Icon icon={FaEdit} />
                                                </Button>
                                            </OverlayTrigger>

                                            <OverlayTrigger overlay={<Tooltip>{t("chatbots_edit.history")}</Tooltip>}>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="icon-btn"
                                                    onClick={() => onShowHistory(source)}
                                                >
                                                    <Icon icon={FaHistory} />
                                                </Button>
                                            </OverlayTrigger>

                                            <OverlayTrigger overlay={<Tooltip>{t("delete")}</Tooltip>}>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="icon-btn"
                                                    onClick={() => onDelete(source)}
                                                    disabled={!isEditMode}
                                                >
                                                    <Icon icon={FaTrash} />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </Table>
        </div>
    );
};

export default SourcesListTable;
